import MojitoCore from 'mojito/core';

import {resolveBackground} from '#core/utils/color-utils.js';
import {borderStyle, icon} from '#core/utils/style-utils.js';

import {generateSecondaryButton, generateToggleButton} from '#core/application/modules/tiles/button/index.js';
import {generatePivotsRow} from '#core/application/modules/tiles/pivot/index.js';
import {generateStreamingIndicator} from '#core/application/modules/tiles/streaming-indicator/index.js';
import {generateTertiaryDropdown} from '#core/application/modules/tiles/dropdown/index.js';
import {generateEmptyContentMessage} from '#core/application/modules/tiles/empty-content-message/index.js';
import {generateBestOddsGuaranteedIndicator} from '#core/application/modules/tiles/best-odds-guaranteed-indicator/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';
import {generatePromotionQuickLinks} from '#core/application/modules/tiles/promotion-quick-links/index.js';

const merge = MojitoCore.Presentation.uiStyle.merge;

export function raceMeetingsFactory(PALETTE) {
    const {FONT} = PALETTE;
    const RACE_EXPLANATION_PANE_STYLE = {
        explanationItem: {
            style: {
                alignItems: 'center',
            },
        },
        explanationText: {
            style: {
                fontSize: FONT.SIZE_XSMALL,
                ...PALETTE.RACE_MEETINGS_EXPLANATION_TEXT,
            },
        },
        resultRaceIcon: icon(PALETTE.RACE_MEETINGS_PANE_ICON_COLOR),
        raceOffIcon: icon(PALETTE.RACE_MEETINGS_LIVE_RACE_ICON_COLOR),
        bestOddsRaceIcon: icon(PALETTE.RACE_MEETINGS_PANE_ICON_COLOR),
        pricedRaceIcon: icon(PALETTE.RACE_MEETINGS_RACING_PRICE_ICON_COLOR),
    };
    const RACE_COUPONS_HIGHLIGHT_EXPLANATION_PANE_STYLE = {
        ...RACE_EXPLANATION_PANE_STYLE,
        explanationText: {
            style: {
                fontSize: FONT.SIZE_XSMALL,
                ...PALETTE.RACE_MEETINGS_HIGHLIGHT_EXPLANATION_TEXT,
            },
        },
        resultRaceIcon: icon(PALETTE.RACE_MEETINGS_HIGHLIGHT_PANE_ICON_COLOR),
        bestOddsRaceIcon: icon(PALETTE.RACE_MEETINGS_HIGHLIGHT_PANE_ICON_COLOR),
        pricedRaceIcon: icon(PALETTE.RACE_MEETINGS_RACING_PRICE_ICON_COLOR),
    };

    return {
        MeetingsView: {
            raceCoupons: {
                viewType: 'meetings',
            },
            filtersBar: {
                defaultNamingSchemaForDayFilters: GENBET_BASED ? 'EEE d' : 'EEEE',
                filter: {
                    button: generateToggleButton(PALETTE.RACE_MEETINGS_FILTER_BUTTON, true),
                },
                topMenuSkeleton: {
                    buttonSkeleton: generateSkeletonPart({
                        ...PALETTE.RACE_MEETINGS_FILTER_BUTTON_SKELETON,
                        SKELETON_BORDER_RADIUS: PALETTE.RACE_MEETINGS_FILTER_BUTTON.TOGGLE_BUTTON_BORDER_RADIUS,
                    }),
                },
            },
        },
        HighlightsView: {
            menuLinkPromotions: merge(generatePromotionQuickLinks(PALETTE.HIGHLIGHTS_PROMOTION_QUICK_LINKS), {
                menuRow: {
                    itemInnerContainer: {
                        style: {
                            padding: '0 8px',
                        },
                    },
                },
            }),
        },
        RaceCouponsView: {
            collapsibleMeetingPane: {
                container: {
                    style: {
                        ...resolveBackground(PALETTE.RACE_MEETINGS_CONTENT_CONTAINER_BG),
                        boxShadow: PALETTE.RACE_MEETINGS_CONTENT_CONTAINER_SHADOW,
                        borderRadius: PALETTE.RACE_MEETING_CONTAINER_BORDER_RADIUS,
                    },
                },
            },
        },
        RaceCouponsSkeleton: {
            container: {
                separator: {
                    style: {
                        size: '1px',
                        color: PALETTE.RACE_MEETINGS_SEPARATOR_COLOR,
                    },
                },
            },
            titleText: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    ...PALETTE.RACE_MEETINGS_HEADER_TEXT,
                },
            },
        },
        RaceMeetingsCouponsView: {
            todayTomorrowMenu: generatePivotsRow(PALETTE.RACE_MEETINGS_TODAY_TOMORROW_SELECTOR),
            titleText: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    ...PALETTE.RACE_MEETINGS_HEADER_TEXT,
                },
            },
            style: {
                headerContainer: {
                    height: '40px',
                },
            },
            raceExplanationPane: {
                ...RACE_EXPLANATION_PANE_STYLE,
            },
        },
        RaceMeetingCoupon: {
            style: {
                allRaces: {
                    container: {
                        ...resolveBackground(PALETTE.RACE_MEETINGS_CONTENT_CONTAINER_BG),
                    },
                    meetingName: {
                        textStyles: {
                            fontSize: FONT.SIZE_MEDIUM,
                            fontWeight: FONT.WEIGHT_SEMI_BOLD,
                            ...PALETTE.RACE_MEETINGS_MEETING_NAME_TEXT,
                        },
                    },
                    meetingNameHover: {
                        textStyles: {
                            fontSize: FONT.SIZE_MEDIUM,
                            fontWeight: FONT.WEIGHT_SEMI_BOLD,
                            ...PALETTE.RACE_MEETINGS_MEETING_NAME_TEXT,
                        },
                    },
                    trackStatus: {
                        textStyles: {
                            fontSize: FONT.SIZE_XXSMALL,
                            ...PALETTE.RACE_MEETINGS_STATUS_TEXT,
                        },
                    },
                    trackStatusHover: {
                        textStyles: {
                            fontSize: FONT.SIZE_XXSMALL,
                            ...PALETTE.RACE_MEETINGS_STATUS_TEXT,
                        },
                    },
                },
                nextRace: {
                    container: {
                        ...resolveBackground(PALETTE.RACE_MEETINGS_CONTENT_CONTAINER_BG),
                        padding: '0 0px 0px 16px',
                    },
                    meetingName: {
                        textStyles: {
                            fontSize: FONT.SIZE_MEDIUM,
                            ...PALETTE.RACE_MEETINGS_MEETING_NAME_TEXT,
                        },
                    },
                    raceStatus: {
                        textStyles: {
                            fontSize: FONT.SIZE_SMALL,
                            ...PALETTE.RACE_MEETINGS_STATUS_TEXT,
                        },
                    },
                },
            },
            raceButton: {
                style: {
                    base: {
                        ...borderStyle(0, PALETTE.RACE_MEETINGS_RACE_BUTTON.TOGGLE_BUTTON_BORDER_COLOR),
                        fontSize: FONT.SIZE_MEDIUM,
                        fontWeight: FONT.WEIGHT_SEMI_BOLD,
                        minHeight: 40,
                        whiteSpace: 'nowrap',
                        ...PALETTE.RACE_MEETINGS_RACE_BUTTON.TOGGLE_BUTTON_TEXT,
                    },
                },
            },
            nextRaceRightIcon: {
                style: {
                    fill: PALETTE.RACE_MEETINGS_NEXT_RACE_ICON_COLOR,
                    minWidth: '8px',
                    minHeight: '8px',
                    padding: '0px 16px 0px 8px',
                },
            },
            finishedRaceButtonIcon: icon(PALETTE.RACE_MEETINGS_FINISHED_RACE_ICON_COLOR),
            liveRaceButtonIcon: icon(PALETTE.RACE_MEETINGS_LIVE_RACE_ICON_COLOR),
            bestOddsRaceButtonIcon: icon(PALETTE.RACE_MEETINGS_BEST_ODDS_RACE_ICON_COLOR),
            pricedRaceButtonIcon: merge(icon(PALETTE.RACE_MEETINGS_RACING_PRICE_ICON_COLOR), {
                style: {
                    padding: 0,
                },
            }),
            streamingIndicator: generateStreamingIndicator(PALETTE.RACE_MEETINGS_STREAMING_INDICATOR),
            countdownTime: {
                timerFourDigits: {
                    timeBox: {
                        style: {
                            minWidth: 'initial',
                        },
                    },
                },
            },
            countdownText: {
                style: {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    maxWidth: '120px',
                },
            },
        },
        RaceMeeting: {
            selectedRaceEventContainer: {
                style: {
                    _mobile_padding: '88px 0 0', // override mojito defaults
                },
            },
            watchLive: {
                container: {
                    style: {
                        padding: '8px 8px 8px 0px',
                        whiteSpace: 'nowrap',
                    },
                },
                button: merge(generateSecondaryButton(PALETTE.RACE_MEETINGS_WATCH_LIVE_BUTTON), {
                    style: {
                        base: {
                            padding: '8px',
                            width: 'auto',
                        },
                    },
                }),
                icon: {
                    src: 'streaming',
                    style: {
                        fill: PALETTE.RACE_MEETINGS_WATCH_LIVE_BUTTON.SECONDARY_BUTTON_ICON_COLOR,
                        minWidth: '16px',
                        minHeight: '16px',
                        marginRight: '8px',
                    },
                },
            },
            pageHeader: {
                headerContainer: {
                    style: {
                        ...resolveBackground(PALETTE.RACE_MEETING_MOBILE_PAGE_HEADER_CONTAINER_BG),
                    },
                },
            },
            dropdown: merge(generateTertiaryDropdown(PALETTE.RACE_MEETING_MOBILE_COMPETITIONS_DROPDOWN), {
                controlButton: {
                    style: {
                        base: {
                            padding: 0,
                        },
                    },
                },
                controlText: {
                    style: {
                        textTransform: 'inherit',
                    },
                },
                itemsContainer: {
                    style: {
                        base: {
                            // width should be the same as flexBasis
                            width: '235px',
                        },
                    },
                },
                rotation: {
                    image: {
                        style: {
                            minWidth: 12,
                            minHeight: 12,
                        },
                    },
                },
            }),
            fixedCarousel: {
                style: {
                    boxShadow: PALETTE.RACE_MEETING_MOBILE_RACE_TIME_SELECTOR_SHADOW,
                    borderStyle: 'solid',
                    borderWidth: '0 0 1px 0',
                    borderColor: PALETTE.RACE_MEETING_MOBILE_RACE_TIME_SELECTOR_BG,
                    ...resolveBackground(PALETTE.RACE_MEETING_MOBILE_RACE_TIME_SELECTOR_BG),
                },
            },
            raceLinks: merge(generatePivotsRow(PALETTE.RACE_MEETING_MOBILE_RACE_TIME_SELECTOR), {
                style: {
                    container: {
                        width: '100%',
                    },
                },
            }),
            selectedRaceCard: {
                style: {
                    headerSeparator: {
                        backgroundColor: 'transparent',
                        height: '0px',
                    },
                    runnerSeparator: {
                        height: '1px',
                    },
                },
            },
            emptyContent: generateEmptyContentMessage(PALETTE.RACE_MEETING_EMPTY_CONTENT),
            bestOddsGuaranteed: {
                popup: generateBestOddsGuaranteedIndicator(PALETTE.RACE_MEETING_BEST_ODDS_GUARANTEED_INDICATOR),
            },
            description: {
                style: {
                    alignItems: 'baseline',
                },
            },
        },
        CollapsibleMeetingPane: {
            title: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_SEMI_BOLD,
                    ...PALETTE.RACE_MEETINGS_MEETING_NAME_TEXT,
                },
            },
            locationsButtonBar: {
                button: generateToggleButton(PALETTE.RACE_MEETINGS_LOCATION_BUTTON, true),
            },
            expandableView: {
                rotation: {
                    image: icon(PALETTE.RACE_MEETINGS_HIGHLIGHT_PANE_ICON_COLOR),
                },
            },
            meetingContentContainer: {
                separator: {
                    style: {
                        color: PALETTE.RACE_MEETINGS_SEPARATOR_COLOR,
                    },
                },
            },
            raceExplanationPane: {
                ...RACE_COUPONS_HIGHLIGHT_EXPLANATION_PANE_STYLE,
            },
        },
    };
}
